import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  TrendingUp, Headphones, Globe, Wrench, Receipt, Database, Bot, Megaphone,
  Check, ArrowRight, ArrowLeft, LayoutTemplate, Lock,
} from "lucide-react";

const ICONS = { "trending-up": TrendingUp, headphones: Headphones, globe: Globe, wrench: Wrench, receipt: Receipt, database: Database, bot: Bot, megaphone: Megaphone };
const STEPS = ["Customer", "Project", "Salesforce Product", "Template", "Assign"];

export default function CreateProjectWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [meta, setMeta] = useState({ products: [], implementation_types: [] });
  const [consultants, setConsultants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer: { name: "", industry: "", website: "", country: "", primary_contact: "", email: "" },
    project_name: "", implementation_type: "New Salesforce Implementation",
    product_key: "sales_cloud", template_id: "tmpl-sales-cloud-core",
    consultant_id: "", participants: "",
  });

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/users?role=consultant").then((r) => setConsultants(r.data));
  }, []);

  const setCust = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return form.customer.name.trim();
    if (step === 1) return form.project_name.trim();
    if (step === 2) return form.product_key;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        participants: form.participants ? form.participants.split(",").map((s) => s.trim()).filter(Boolean) : [],
        consultant_id: form.consultant_id || null,
      };
      const { data } = await api.post("/projects", payload);
      toast.success("Discovery project created");
      navigate(`/projects/${data.id}`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="New Discovery Project" subtitle="Set up a guided discovery in five quick steps." breadcrumb={["Projects", "New"]} />
      <div className="flex-1 px-6 py-8 lg:px-10">
        <div className="mx-auto max-w-3xl">
          {/* Stepper */}
          <div className="mb-8 flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s} className="flex flex-1 items-center">
                <div className="flex flex-col items-center gap-1.5">
                  <div className={cn("flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-colors",
                    i < step ? "bg-sky-600 text-white" : i === step ? "bg-sky-600 text-white ring-4 ring-sky-100" : "bg-slate-100 text-slate-400")}>
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className={cn("hidden text-[11px] font-semibold sm:block", i <= step ? "text-slate-700" : "text-slate-400")}>{s}</span>
                </div>
                {i < STEPS.length - 1 && <div className={cn("mx-2 h-0.5 flex-1 rounded", i < step ? "bg-sky-600" : "bg-slate-200")} />}
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8">
            {step === 0 && (
              <div className="space-y-5">
                <h2 className="font-heading text-xl font-bold text-slate-900">Tell us about the customer</h2>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Customer Name" required><Input data-testid="cust-name" value={form.customer.name} onChange={(e) => setCust("name", e.target.value)} placeholder="ABC Manufacturing Pvt Ltd" /></Field>
                  <Field label="Industry">
                    <Select value={form.customer.industry} onValueChange={(v) => setCust("industry", v)}>
                      <SelectTrigger data-testid="cust-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>
                        {["manufacturing", "financial", "healthcare", "technology", "retail", "realestate", "services", "other"].map((i) => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Website"><Input data-testid="cust-website" value={form.customer.website} onChange={(e) => setCust("website", e.target.value)} placeholder="https://…" /></Field>
                  <Field label="Country"><Input data-testid="cust-country" value={form.customer.country} onChange={(e) => setCust("country", e.target.value)} placeholder="India" /></Field>
                  <Field label="Primary Contact"><Input data-testid="cust-contact" value={form.customer.primary_contact} onChange={(e) => setCust("primary_contact", e.target.value)} placeholder="Rajesh Kumar" /></Field>
                  <Field label="Email"><Input data-testid="cust-email" type="email" value={form.customer.email} onChange={(e) => setCust("email", e.target.value)} placeholder="contact@company.com" /></Field>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-heading text-xl font-bold text-slate-900">Project details</h2>
                <Field label="Project Name" required><Input data-testid="project-name" value={form.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="ABC Manufacturing — Sales Cloud Implementation" /></Field>
                <Field label="Implementation Type">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {meta.implementation_types.map((t) => (
                      <button key={t} type="button" data-testid={`impl-${t.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => set("implementation_type", t)}
                        className={cn("rounded-xl border p-4 text-left text-sm font-semibold transition-colors",
                          form.implementation_type === t ? "border-sky-500 bg-sky-50 text-sky-800 ring-1 ring-sky-500" : "border-slate-200 hover:border-sky-300")}>
                        {t}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <h2 className="font-heading text-xl font-bold text-slate-900">Which Salesforce product?</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {meta.products.map((p) => {
                    const Icon = ICONS[p.icon] || TrendingUp;
                    const selected = form.product_key === p.key;
                    return (
                      <button key={p.key} type="button" data-testid={`product-${p.key}`} disabled={!p.available}
                        onClick={() => p.available && set("product_key", p.key)}
                        className={cn("relative flex flex-col items-start rounded-xl border p-5 text-left transition-colors",
                          !p.available ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-70" :
                          selected ? "border-sky-500 bg-sky-50 ring-1 ring-sky-500" : "border-slate-200 hover:border-sky-300")}>
                        {!p.available && <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500"><Lock className="h-2.5 w-2.5" /> Soon</span>}
                        <div className={cn("mb-3 flex h-11 w-11 items-center justify-center rounded-xl", selected ? "brand-gradient text-white" : "bg-slate-100 text-slate-500")}><Icon className="h-5 w-5" /></div>
                        <div className="font-heading text-base font-bold text-slate-900">{p.name}</div>
                        <div className="mt-1 text-xs text-slate-500">{p.description}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <h2 className="font-heading text-xl font-bold text-slate-900">Discovery template</h2>
                <div className="flex items-start gap-4 rounded-xl border border-sky-500 bg-sky-50 p-5 ring-1 ring-sky-500">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient text-white"><LayoutTemplate className="h-5 w-5" /></div>
                  <div>
                    <div className="flex items-center gap-2"><span className="font-heading text-base font-bold text-slate-900">Sales Cloud Core Discovery</span><Check className="h-4 w-4 text-sky-600" /></div>
                    <p className="mt-1 text-sm text-slate-600">10 modules · guided Learn → Discover → Review journey covering leads, accounts, opportunities, activities, products, reporting, data, integrations and security.</p>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <h2 className="font-heading text-xl font-bold text-slate-900">Assign the team</h2>
                <Field label="Assigned Consultant">
                  <Select value={form.consultant_id} onValueChange={(v) => set("consultant_id", v)}>
                    <SelectTrigger data-testid="assign-consultant"><SelectValue placeholder="Select a consultant" /></SelectTrigger>
                    <SelectContent>{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Customer Participants (comma-separated emails)">
                  <Input data-testid="assign-participants" value={form.participants} onChange={(e) => set("participants", e.target.value)} placeholder="rajesh@abcmanufacturing.com" />
                </Field>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
              <Button variant="ghost" data-testid="wizard-back" disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="text-slate-500">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="rounded-lg bg-sky-600 hover:bg-sky-700">
                  Continue <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              ) : (
                <Button data-testid="wizard-create" disabled={busy} onClick={submit} className="rounded-lg bg-sky-600 hover:bg-sky-700">
                  {busy ? "Creating…" : "Create Project"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
